import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {PGPApi, PGPKeyDetails, PGPKeyringApi} from "components/src/api/admin/generated";
import {DialogCloseCause} from "components/src/common/Enums";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {logger} from "components/src/utils/Logger";
import {Utils} from "components/src/utils/Utils";
import {Bin as DeleteIcon} from "iconoir-react";
import {useEffect, useState} from "react";
import {Navbar} from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import Modal from "react-bootstrap/Modal";
import Nav from "react-bootstrap/Nav";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";
import Table from "react-bootstrap/Table";
import {useNavigate} from "react-router-dom";
import {PGPKeyRow} from "./PGPKeyRow";
import {PGPKeyTableHeader} from "./PGPKeyTableHeader";

const pgpKeyringApi = new PGPKeyringApi(ApiConfig.config());
const pgpApi = new PGPApi(ApiConfig.config());

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface PGPKeyDetailsProps {
    pgpKey : PGPKeyDetails
    onClose : OnClose
}

export const PGPKeyDetailsDialogs = ({pgpKey, onClose} : PGPKeyDetailsProps) =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * The PGP master key
     */
    const [masterKey, setMasterKey] = useState<PGPKeyDetails>();
    /**
     * The loaded PGP keys
     */
    const [subkeys, setSubkeys] = useState<PGPKeyDetails[]>([]);
    /**
     * All emails addresses from the key
     */
    const [emails, setEmails] = useState<string[]>();
    /**
     * The selected email addresses
     */
    const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
    /**
     * new address
     */
    const [newAddress, setNewAddress] = useState<string>();
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * Set to true while saving to show a spinner on the import button
     */
    const [saving, setSaving] = useState<boolean>(false);
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    // load the master key if the key is not a master key
    useEffect(() =>
    {
        if (pgpKey.masterKey)
        {
            setMasterKey(pgpKey);
            setEmails([...pgpKey.email]);
            return;
        }

        if (pgpKey.parentID === undefined) {
            // should not happen
            logger.error("PGP key has no parent ID");
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        pgpKeyringApi.pGPKeyringControllerGetKey(pgpKey.parentID)
            .then(response =>
            {
                setEmails([...response.data.email]);
                setMasterKey(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting master key", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        pgpKey,
        setSpinnerStateCounter,
        t]);

    useEffect(() =>
    {
        if (masterKey === undefined) {
            return;
        }

        logger.debug("Getting PGP sub key count")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        pgpKeyringApi.pGPKeyringControllerGetSubkeys(masterKey.id)
            .then(response => {
                setSubkeys(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting sub keys", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        masterKey,
        setSpinnerStateCounter,
        t
    ]);


    // called when the dialog is closed without clicking export
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    /**
     * Called when an email address is selected (i.e., when the checkbox is checked)
     */
    const onSelectEmail = (checked : boolean, email : string) =>
    {
        if (checked) {
            // add to selected
            setSelectedEmails([...selectedEmails, email]);
        }
        else {
            // remove from selected
            setSelectedEmails(selectedEmails.filter(e => e !== email));
        }
    }

    const onRemoveSelected = () =>
    {
        if (emails === undefined) {
            return;
        }

        setEmails([...emails].filter(email => !selectedEmails.includes(email)));
        setSelectedEmails([]);
    }

    const onAddNewAddress = (address : string) =>
    {
        if (emails === undefined) {
            return;
        }

        if (!emails.includes(address)) {
            setEmails([...emails, address]);
        }
    }

    const onSave = () =>
    {
        if (masterKey === undefined || emails === undefined) {
            return;
        }

        setSaving(true);

        pgpApi.pGPControllerSetPGPKeyEmailAndDomains(masterKey.id, emails)
            .then(() => {
                onClose(DialogCloseCause.Succes);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error setting email and domains", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                setSaving(false);
            });
    }

    return (
        <Modal show={true} onHide={() => onHideDialog()} size="xl" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{t('pgp-details-dialog.title', {pgpkeyid: masterKey?.keyID})} </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row>
                    <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>
                </Row>
                <Row>
                    <Table bordered hover responsive>
                        <thead className="sticky-top">
                            <tr className="text-nowrap">
                                {selectedEmails.length === 0 ?
                                    <th>{t("pgp-details-dialog.email-domain")}</th>
                                    :
                                    <th colSpan={1}>
                                        <Navbar className="bg-body-tertiary ps-2">
                                            <Navbar.Text className="ms-2">{selectedEmails.length} selected</Navbar.Text>

                                            <div className="vr ms-4 me-2"></div>

                                            <Nav>
                                                <Nav.Link onClick={() => onRemoveSelected()}>
                                                    <DeleteIcon className="text-danger"/> {t("pgp-details-dialog.remove-selected")}
                                                </Nav.Link>
                                            </Nav>
                                        </Navbar>
                                    </th>
                                }
                            </tr>
                        </thead>
                        <tbody>
                            {emails?.map(email =>
                                <tr key={email}>
                                    <td>
                                        <Form.Check
                                            type="checkbox"
                                            checked={selectedEmails.includes(email)}
                                            onChange={(e) => onSelectEmail(e.target.checked, email)}
                                            inline
                                        />
                                        {email}
                                    </td>
                                </tr>)
                            }
                        </tbody>
                    </Table>
                </Row>
                <Row>
                    <Col md={6}>
                        <InputGroup>
                            <Form.Control aria-label={t("pgp-details-dialog.email-or-domain")}
                                          type="text"
                                          placeholder={t("pgp-details-dialog.new-email-or-domain-address")}
                                          value={newAddress}
                                          onChange={(e) => setNewAddress(e.target.value)}
                            />
                            <Button variant="primary"
                                    onClick={() => newAddress !== undefined && onAddNewAddress(newAddress.toLowerCase())}
                                    disabled={newAddress?.length === 0}>
                                {t("pgp-details-dialog.add")}
                            </Button>
                        </InputGroup>
                    </Col>
                </Row>
                <Row>
                    <div className="mt-3">
                        <Table className="caption-top" bordered hover responsive>
                            <caption>{t("pgp-details-dialog.subkeys")}</caption>
                            <PGPKeyTableHeader/>
                            <tbody>
                            {subkeys.map((key: PGPKeyDetails) => (
                                <PGPKeyRow key={key.fingerprint} pgpKey={key}/>
                            ))}
                            </tbody>
                        </Table>
                    </div>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success"
                        onClick={onSave}
                        // save should be enabled if the email address is different from the pgp email addresses
                        disabled={masterKey !== undefined && emails?.length === [...masterKey.email].length &&
                            [...masterKey.email].every(email => emails.includes(email))}>
                    {saving && <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        aria-hidden="true"
                        className="me-1"
                    />}
                    {saving ? t("common.saving") :  t("common.save")}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
